import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Briefcase, Loader2, Plus, Search } from "lucide-react";
import { adminCaseStats, adminListCases } from "@/lib/api/admin-cases.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/cases/")({
  head: () => ({
    meta: [
      { title: "Case management · NDLCS Admin" },
      {
        name: "description",
        content: "Central hub for every client matter from enquiry to file closure.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminCases,
});

const PRIORITY_STYLES: Record<string, string> = {
  urgent: "bg-red-100 text-red-800",
  high: "bg-amber-100 text-amber-800",
  normal: "bg-slate-100 text-slate-700",
  low: "bg-emerald-100 text-emerald-800",
};

function AdminCases() {
  const stats = useServerFn(adminCaseStats);
  const list = useServerFn(adminListCases);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [applied, setApplied] = useState({ q: "", status: "", priority: "" });

  const statsQuery = useQuery({ queryKey: ["admin-case-stats"], queryFn: () => stats() });
  const casesQuery = useQuery({
    queryKey: ["admin-cases", applied],
    queryFn: () =>
      list({
        data: {
          q: applied.q || undefined,
          status: applied.status || undefined,
          priority: applied.priority || undefined,
        },
      }),
  });

  const s = statsQuery.data;

  return (
    <div className="space-y-5 py-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
            <Briefcase className="h-3.5 w-3.5" /> Admin
          </p>
          <h1 className="font-display text-2xl text-navy">Case management</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every client matter from first enquiry to file closure, with a full audit trail.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/portal/admin/cases/reports"
            className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            Reports
          </Link>
          <Link
            to="/portal/admin/cases/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-2 text-sm font-medium text-white hover:bg-navy/90"
          >
            <Plus className="h-4 w-4" /> New matter
          </Link>
        </div>

      </header>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: "Total matters", value: s?.total },
          { label: "Open", value: s?.open },
          { label: "Closed", value: s?.closed },
          { label: "Urgent", value: s?.urgent },
          { label: "Overdue", value: s?.overdue },
          { label: "Tasks due", value: s?.dueTasks },
        ].map((c) => (
          <div key={c.label} className="rounded-lg border border-border bg-card p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</p>
            <p className="mt-1 text-2xl font-semibold text-navy">{c.value ?? "—"}</p>
          </div>
        ))}
      </div>

      <form
        className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-3"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ q, status, priority });
        }}
      >
        <div className="min-w-[220px] flex-1">
          <label htmlFor="case-q" className="text-xs font-medium text-muted-foreground">
            Search
          </label>
          <input
            id="case-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Matter number, title, client…"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="case-status" className="text-xs font-medium text-muted-foreground">
            Status
          </label>
          <select
            id="case-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_review">In review</option>
            <option value="in_progress">In progress</option>
            <option value="awaiting_client">Awaiting client</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label htmlFor="case-priority" className="text-xs font-medium text-muted-foreground">
            Priority
          </label>
          <select
            id="case-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </div>
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-2 text-sm font-medium hover:bg-muted"
        >
          <Search className="h-4 w-4" /> Filter
        </button>
      </form>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        {casesQuery.isPending ? (
          <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading matters…
          </p>
        ) : casesQuery.error ? (
          <p className="p-4 text-sm text-red-700">{String((casesQuery.error as any).message)}</p>
        ) : !casesQuery.data?.matters.length ? (
          <p className="p-4 text-sm text-muted-foreground">
            No managed matters yet. Register your first matter to get started.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Matter</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Priority</th>
                <th className="px-3 py-2">Opened</th>
                <th className="px-3 py-2">Due</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {casesQuery.data.matters.map((m: any) => (
                <tr key={m.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <p className="font-medium text-navy">{m.title}</p>
                    <p className="text-xs text-muted-foreground">{m.matter_number}</p>
                  </td>
                  <td className="px-3 py-2">
                    <p>{m.client_name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{m.client_email}</p>
                  </td>
                  <td className="px-3 py-2 text-xs">{m.matter_type || m.category}</td>
                  <td className="px-3 py-2 text-xs">{String(m.status).replace(/_/g, " ")}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        PRIORITY_STYLES[m.priority] ?? PRIORITY_STYLES["normal"]
                      }`}
                    >
                      {m.priority}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{m.date_opened}</td>
                  <td className="px-3 py-2 text-xs">{m.due_date || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to="/portal/admin/cases/$id"
                      params={{ id: m.id }}
                      className="rounded-md border border-input px-2 py-1 text-xs font-medium hover:bg-muted"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
